import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { fetchFormFields, postFormData } from '../../../../modules/api';
import { useRecaptchaToken } from '../../../globalHooks/useRecaptchaToken';
import useParams from '../../../globalHooks/useParams';

const useDesignMyTripForm = () => {
  const params = useParams();

  const [fields, setFields] = useState({});

  const [waiting, setWaiting] = useState(true);

  const [submitInfo, setSubmitInfo] = useState(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [initialValues, setInitialValues] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    how_hear: null,
    style: [],
    destinations: [],
    accommodation: [],
    time: '',
    people: null,
    tell_more_dest: '',
    tell_more_style: '',
    tell_more_accomm: '',
    tell_more_people: '',
    budget: null,
    opt_in: false,
    honeypot: null,
    current_page: window.location.href,
    referrer: document.referrer,
    client_id: 'No client id detected',
  });

  const [cookies] = useCookies(['_ga']);
  const executeRecaptcha = useRecaptchaToken();

  // Get _ga client id
  const getGaClientId = () => {
    const gaCookie = cookies._ga;

    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length === 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }

    return 'No client id detected';
  };

  // Set client id when _ga cookie is available
  useEffect(() => {
    const gaClientId = getGaClientId();

    setInitialValues((prevValues) => ({
      ...prevValues,
      client_id: gaClientId,
    }));
  }, [cookies._ga]);

  const validationSchema = Yup.object({
    destinations: Yup.array().min(1).required('* required'),
    first_name: Yup.string().required('* required'),
    last_name: Yup.string().required('* required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
    phone: Yup.string()
      .required('* required')
      .matches(/^\+?\s*\d+(\s+\d+)*\s*$/, 'Please enter a valid phone number'),
  });

  // Get fields on load
  useEffect(() => {
    (async () => {
      const data = await fetchFormFields('design');

      setFields(data);
      setSubmitInfo(data.thank_you);

      setWaiting(false);
    })();
  }, []);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available');
      return null;
    }

    const token = await executeRecaptcha('design');

    return token;
  }, [executeRecaptcha]);

  const postForm = async (data, recaptchaToken, callback) => {
    if (!recaptchaToken || recaptchaToken.trim().length < 1) {
      alert('Please fill out the Recaptcha');
    } else {
      setWaiting(true);

      try {
        const extraFields = {
          form_reference: 'design',
          id: 'design-form',
        };

        await postFormData({ ...data, ...extraFields }, recaptchaToken);

        if (callback) callback();

        if (submitInfo.type === 'page') {
          window.location.href = submitInfo.page;
        } else {
          setShowSuccessDialog(true);
        }
      } catch (error) {
        console.error(error);

        if (error.response?.data?.message?.includes('recaptcha')) {
          alert('Invalid recaptcha response');
        }
      }

      setWaiting(false);
    }
  };

  const handleCheckboxGroupChange = (
    value,
    currentValues,
    setFieldValue,
    field
  ) => {
    let outputValues;

    if (currentValues.includes(value)) {
      outputValues = currentValues.filter((element) => element !== value);
    } else {
      outputValues = [...currentValues, value];
    }

    setFieldValue(field, outputValues);
  };

  const disable = (errors, values, initialValues) => {
    const hasErrors = Object.keys(errors).length > 0;

    return (
      hasErrors || JSON.stringify(values) === JSON.stringify(initialValues)
    );
  };

  return {
    validationSchema,
    initialValues,
    disable,
    postForm,
    fields,
    handleCheckboxGroupChange,
    waiting,
    showSuccessDialog,
    setShowSuccessDialog,
    handleReCaptchaVerify,
  };
};

export default useDesignMyTripForm;
